/**
 * ColourCodesSection — per-org PixelWrangler colour-code alias map (Phase 63).
 *
 * Edits the `colourAliases` table the bootleg-bridge fetches. The bridge's
 * hardcoded codes (WHT → WH, GR → GN, BL) are the FLOOR and always work with
 * nothing here; rows added here are merged over that floor. Two kinds:
 *   - Rewrite      (canonical_code set)  → bridge rewrites the filename code.
 *   - Recognise    (canonical_code null) → bridge stops skipping the code.
 *
 * RBAC: the Production Settings route is coordinator+ (RoleGuard), and the
 * backend `list` is OrgSettingsRead — so coordinators SEE this read-only.
 * `upsert`/`delete` are adminProcedure, so the write affordances are gated on
 * `useRole().isAdmin()` (mirrors ProductDataLookupCard). The backend is the
 * source of truth either way.
 *
 * Mirrors ProductTypeAliasSection (monospace codes, ArrowRight glyph,
 * floor-framing empty state) with a RadioGroup add-modal + per-row delete.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { ArrowRight, Info, Loader2, Plus, Trash2 } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useRole } from '@/hooks/use-role';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { AddColourCodeModal } from './AddColourCodeModal';

export function ColourCodesSection() {
  const { isAdmin } = useRole();
  const admin = isAdmin();
  const [addOpen, setAddOpen] = useState(false);

  const listQuery = trpc.colourAliases.list.useQuery();
  const rows = listQuery.data ?? [];
  const existingCodes = rows.map((r) => r.pwCode.toUpperCase());

  const utils = trpc.useUtils();

  const upsertMutation = trpc.colourAliases.upsert.useMutation({
    onSuccess: () => {
      toast.success('Colour code saved');
      utils.colourAliases.list.invalidate();
      setAddOpen(false);
    },
    onError: (e) => toast.error(`Could not save: ${e.message}`),
  });

  const deleteMutation = trpc.colourAliases.delete.useMutation({
    onSuccess: () => {
      toast.success('Colour code removed');
      utils.colourAliases.list.invalidate();
    },
    onError: (e) => toast.error(`Could not remove: ${e.message}`),
  });

  return (
    <div className="space-y-4">
      {/* Floor + refresh-lag framing. */}
      <Alert>
        <Info className="h-4 w-4" aria-hidden="true" />
        <AlertDescription className="text-sm leading-relaxed">
          Built-in codes (<span className="font-mono">WHT&nbsp;→&nbsp;WH</span>,{' '}
          <span className="font-mono">GR&nbsp;→&nbsp;GN</span>, <span className="font-mono">BL</span>) keep
          working with nothing here. Add a code only when PixelWrangler sends one the bridge skips, for
          example when it reports an unrecognised colour code in Slack. Changes reach the bridge on its next
          refresh (within&nbsp;12&nbsp;hours) or when it restarts.
        </AlertDescription>
      </Alert>

      {listQuery.isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
        </div>
      ) : (
        <>
          {rows.length > 0 ? (
            <div className="overflow-hidden rounded-lg border">
              <table className="w-full text-base">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-3 text-left font-medium">PixelWrangler code</th>
                    <th className="w-8 px-2 py-3" />
                    <th className="px-4 py-3 text-left font-medium">Result</th>
                    <th className="px-4 py-3 text-left font-medium">Type</th>
                    {admin ? <th className="w-14" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const isRewrite = row.canonicalCode != null;
                    return (
                      <tr key={row.id} className="border-b last:border-b-0 align-top">
                        <td className="px-4 py-3 font-mono text-sm">{row.pwCode}</td>
                        <td className="px-2 py-3 text-muted-foreground">
                          {isRewrite ? <ArrowRight className="h-4 w-4" aria-hidden="true" /> : null}
                        </td>
                        <td className="px-4 py-3">
                          {isRewrite ? (
                            <span className="font-mono text-sm">{row.canonicalCode}</span>
                          ) : (
                            <span className="text-sm text-muted-foreground">Recognised as-is</span>
                          )}
                          {row.note ? (
                            <p className="mt-0.5 text-xs text-muted-foreground/80">{row.note}</p>
                          ) : null}
                        </td>
                        <td className="px-4 py-3">
                          {isRewrite ? (
                            <Badge variant="secondary">Rewrite</Badge>
                          ) : (
                            <Badge variant="outline">Recognise only</Badge>
                          )}
                        </td>
                        {admin ? (
                          <td className="px-2 py-2">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-11 w-11 text-muted-foreground hover:text-foreground"
                                  aria-label={`Remove colour code ${row.pwCode}`}
                                  disabled={deleteMutation.isPending}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    Remove {row.pwCode}?
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    {isRewrite ? (
                                      <>
                                        The bridge will stop rewriting{' '}
                                        <span className="font-mono">{row.pwCode}</span> to{' '}
                                        <span className="font-mono">{row.canonicalCode}</span> and may
                                        start skipping those files again. You can re-add it any time.
                                      </>
                                    ) : (
                                      <>
                                        The bridge will stop recognising{' '}
                                        <span className="font-mono">{row.pwCode}</span> and may start
                                        skipping those files again. You can re-add it any time.
                                      </>
                                    )}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Keep it</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-orange-600 text-white hover:bg-orange-700 focus-visible:ring-orange-600"
                                    onClick={() => deleteMutation.mutate({ pwCode: row.pwCode })}
                                  >
                                    Remove
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="py-2 text-center text-sm text-muted-foreground/70">
              No custom colour codes. The built-in codes keep working with nothing here.
            </p>
          )}

          {admin ? (
            <div className="flex justify-end">
              <Button variant="outline" className="h-11" onClick={() => setAddOpen(true)}>
                <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
                Add colour code
              </Button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground/70">
              Ask an admin to add or change colour codes.
            </p>
          )}
        </>
      )}

      <AddColourCodeModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        existingCodes={existingCodes}
        onAdd={(input) => upsertMutation.mutate(input)}
        isPending={upsertMutation.isPending}
      />
    </div>
  );
}
